import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowRight, CheckCircle2, AlertTriangle, DollarSign } from 'lucide-react';
import { useUpsellConversion, ConvertibleDeal, ConfirmedPayment, UpsellCalculation } from '@/hooks/useUpsellConversion';

interface UpsellConversionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  originDeal: {
    id: string;
    name: string;
    total_amount: number;
    currency: string;
    contactId: string;
  };
  onSuccess?: () => void;
}

type Step = 'select' | 'review' | 'confirm';

const formatCurrency = (value: number, currency: string = 'USD') => {
  return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
  }).format(value);
};

export function UpsellConversionModal({
  open,
  onOpenChange,
  originDeal,
  onSuccess,
}: UpsellConversionModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('select');
  const [selectedDestinationId, setSelectedDestinationId] = useState<string | null>(null);
  const [confirmedPayments, setConfirmedPayments] = useState<ConfirmedPayment[]>([]);
  const [calculation, setCalculation] = useState<UpsellCalculation | null>(null);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const {
    convertibleDeals,
    dealsLoading,
    fetchConfirmedPayments,
    calculateUpsellValues,
    processConversion,
    isProcessing,
  } = useUpsellConversion(originDeal.contactId);

  // Filter out the origin deal from destination options
  const destinationOptions = convertibleDeals?.filter(d => d.id !== originDeal.id) || [];
  const selectedDestination = destinationOptions.find(d => d.id === selectedDestinationId);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('select');
      setSelectedDestinationId(null);
      setConfirmedPayments([]);
      setCalculation(null);
    }
  }, [open]);

  // Load confirmed payments when origin deal is set
  useEffect(() => {
    if (open && originDeal.id) {
      loadPayments();
    }
  }, [open, originDeal.id]);

  const loadPayments = async () => {
    setLoadingPayments(true);
    try {
      const payments = await fetchConfirmedPayments(originDeal.id);
      setConfirmedPayments(payments);
    } catch (err) {
      console.error('Error loading payments:', err);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleSelectDestination = (dealId: string) => {
    setSelectedDestinationId(dealId);
    const dest = destinationOptions.find(d => d.id === dealId);
    if (dest) {
      const calc = calculateUpsellValues(
        { 
          id: originDeal.id, 
          name: originDeal.name, 
          total_amount: originDeal.total_amount,
          currency: originDeal.currency,
          stage: '',
          created_at: '',
        },
        dest.total_amount,
        confirmedPayments
      );
      setCalculation(calc);
    }
  };

  const handleNext = () => {
    if (step === 'select' && selectedDestinationId) {
      setStep('review');
    } else if (step === 'review') {
      setStep('confirm');
    }
  };

  const handleBack = () => {
    if (step === 'review') {
      setStep('select');
    } else if (step === 'confirm') {
      setStep('review');
    }
  };

  const handleConfirm = () => {
    if (!selectedDestinationId || !calculation) return;

    processConversion({
      originDealId: originDeal.id,
      destinationDealId: selectedDestinationId,
      originalDealValue: originDeal.total_amount,
      creditToTransfer: calculation.confirmedPaymentsTotal,
    }, {
      onSuccess: () => {
        onOpenChange(false);
        onSuccess?.();
      },
    });
  };

  const confirmedTotal = confirmedPayments.reduce((sum, p) => sum + p.amount, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Converter para Upsell</DialogTitle>
          <DialogDescription>
            Transfira os pagamentos do deal atual para um novo deal, mantendo o histórico e ajustando os relatórios de vendas.
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-4">
          {['select', 'review', 'confirm'].map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s
                    ? 'bg-primary text-primary-foreground'
                    : i < ['select', 'review', 'confirm'].indexOf(step)
                    ? 'bg-green-500 text-white'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {i < ['select', 'review', 'confirm'].indexOf(step) ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  i + 1
                )}
              </div>
              {i < 2 && <ArrowRight className="w-4 h-4 mx-2 text-muted-foreground" />}
            </div>
          ))}
        </div>

        {/* Step 1: Select destination deal */}
        {step === 'select' && (
          <div className="space-y-4">
            {/* Origin Deal Info */}
            <Card className="bg-muted/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-muted-foreground">Deal Origem (será convertido)</div>
                    <div className="font-medium">{originDeal.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg">{formatCurrency(originDeal.total_amount, originDeal.currency)}</div>
                    {loadingPayments ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <div className="text-sm text-green-600">
                        {formatCurrency(confirmedTotal, originDeal.currency)} pagos
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Separator />

            {/* Destination Deal Selection */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Selecione o deal destino (novo contrato)</Label>
              
              {dealsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : destinationOptions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                  <p>Nenhum outro deal encontrado para este contato.</p>
                  <p className="text-sm">Crie um novo deal primeiro.</p>
                </div>
              ) : (
                <RadioGroup
                  value={selectedDestinationId || ''}
                  onValueChange={handleSelectDestination}
                  className="space-y-2"
                >
                  {destinationOptions.map((deal) => (
                    <div
                      key={deal.id}
                      className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedDestinationId === deal.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/50'
                      }`}
                      onClick={() => handleSelectDestination(deal.id)}
                    >
                      <RadioGroupItem value={deal.id} id={deal.id} />
                      <Label htmlFor={deal.id} className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{deal.name}</div>
                            <div className="text-sm text-muted-foreground">
                              <Badge variant="outline" className="mr-2">{deal.stage}</Badge>
                              {new Date(deal.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="text-right font-bold">
                            {formatCurrency(deal.total_amount, deal.currency)}
                          </div>
                        </div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Review calculation */}
        {step === 'review' && selectedDestination && calculation && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <div className="text-sm text-amber-600 mb-1">Deal Origem</div>
                  <div className="font-medium">{originDeal.name}</div>
                  <div className="text-lg font-bold">{formatCurrency(originDeal.total_amount, originDeal.currency)}</div>
                  <Badge className="mt-2 bg-amber-600">Convertido p/ Upsell</Badge>
                </CardContent>
              </Card>

              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="text-sm text-green-600 mb-1">Deal Destino</div>
                  <div className="font-medium">{selectedDestination.name}</div>
                  <div className="text-lg font-bold">{formatCurrency(selectedDestination.total_amount, selectedDestination.currency)}</div>
                  <Badge variant="default" className="mt-2 bg-green-600">Receberá Créditos</Badge>
                </CardContent>
              </Card>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Pagamentos a transferir:</span>
                <span className="font-medium text-green-600">
                  {confirmedPayments.length} ({formatCurrency(calculation.confirmedPaymentsTotal, originDeal.currency)})
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Saldo restante no novo deal:</span>
                <span className="font-medium">
                  {formatCurrency(calculation.remainingBalance, originDeal.currency)}
                </span>
              </div>

              <Separator />

              <div className="flex justify-between items-center bg-blue-50 p-3 rounded-lg">
                <div>
                  <span className="font-medium text-blue-800">Valor para relatórios do vendedor:</span>
                  <p className="text-sm text-blue-600">
                    (Diferença entre contratos - não conta venda duplicada)
                  </p>
                </div>
                <span className="text-xl font-bold text-blue-800">
                  {formatCurrency(calculation.newSaleValue, originDeal.currency)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 'confirm' && selectedDestination && calculation && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <div className="font-medium text-yellow-800">Confirmar Conversão</div>
                  <p className="text-sm text-yellow-700 mt-1">
                    Esta ação irá:
                  </p>
                  <ul className="text-sm text-yellow-700 list-disc ml-4 mt-2 space-y-1">
                    <li>Marcar o deal "{originDeal.name}" como <strong>convertido para upsell</strong></li>
                    <li>Cancelar parcelas pendentes do deal original (saem do contas a receber)</li>
                    <li>Transferir {confirmedPayments.length} pagamento(s) confirmado(s) ({formatCurrency(calculation.confirmedPaymentsTotal, originDeal.currency)}) para "{selectedDestination.name}"</li>
                    <li>Registrar {formatCurrency(originDeal.total_amount, originDeal.currency)} como crédito de upsell (não conta como venda nova)</li>
                  </ul>
                </div>
              </div>
            </div>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-center gap-4 text-center">
                  <div>
                    <DollarSign className="w-8 h-8 mx-auto text-green-600 mb-2" />
                    <div className="text-sm text-muted-foreground">Nova venda registrada</div>
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(calculation.newSaleValue, originDeal.currency)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step !== 'select' && (
            <Button variant="outline" onClick={handleBack} disabled={isProcessing}>
              Voltar
            </Button>
          )}
          
          {step === 'confirm' ? (
            <Button onClick={handleConfirm} disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                'Confirmar Conversão'
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={step === 'select' && !selectedDestinationId}
            >
              Próximo
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
